(() => {
  if (window.SupplyMeAccessLoaded) {
    return;
  }

  window.SupplyMeAccessLoaded = true;

  const ROOT_SELECTOR =
    "[data-supplyme-buy-box]";

  const FORM_SELECTOR =
    ".supplyme-free-buy-box__form, .supplyme-paid-buy-box__form";

  const CONTEXT_TIMEOUT_MS = 3000;
  const ACTION_TIMEOUT_MS = 4000;

  const NATIVE_SUBMIT_ATTRIBUTE =
    "data-supplyme-native-submit-approved";

  const UPGRADE_RESERVATION_STORAGE_KEY =
    "supplymeUpgradeCreditReservationToken";

  const contextCache =
    new Map();

  let cartPromise = null;

  function getErrorElement(form) {
    return form?.querySelector(
      ".js-form-error"
    ) ?? null;
  }

  function hideError(form) {
    const errorElement =
      getErrorElement(form);

    if (!errorElement) {
      return;
    }

    errorElement.hidden = true;
    errorElement.textContent = "";
  }

  function showError(form, message) {
    const errorElement =
      getErrorElement(form);

    if (!errorElement) {
      window.alert(message);
      return;
    }

    errorElement.textContent =
      message;
    errorElement.hidden = false;

    errorElement.scrollIntoView({
      behavior: "smooth",
      block: "nearest"
    });
  }

  function getPrimaryControl(buyBox) {
    return buyBox.querySelector(
      "[data-supplyme-primary-cta]"
    );
  }

  function setButtonLoading(
    control,
    loading
  ) {
    if (!control) {
      return;
    }

    if (loading) {
      if (
        !control.dataset
          .supplymeOriginalText
      ) {
        control.dataset
          .supplymeOriginalText =
          control.textContent.trim();
      }

      control.setAttribute(
        "aria-busy",
        "true"
      );

      if (
        "disabled" in control
      ) {
        control.disabled = true;
      }

      control.textContent =
        "Checking access...";

      return;
    }

    control.removeAttribute(
      "aria-busy"
    );

    if (
      "disabled" in control
    ) {
      control.disabled = false;
    }
  }

  function withTimeout(
    operation,
    timeoutMs
  ) {
    const controller =
      new AbortController();

    const timeoutId =
      window.setTimeout(
        () => controller.abort(),
        timeoutMs
      );

    return operation(
      controller.signal
    ).finally(() => {
      window.clearTimeout(
        timeoutId
      );
    });
  }

  async function parseJsonResponse(
    response,
    fallbackMessage
  ) {
    const responseText =
      await response.text();

    let responseData = null;

    try {
      responseData =
        JSON.parse(responseText);
    } catch {
      const error =
        new Error(
          fallbackMessage
        );

      error.status =
        response.status;

      error.retryable =
        response.status >= 500;

      throw error;
    }

    if (
      !response.ok ||
      responseData?.success ===
        false ||
      responseData?.approved ===
        false
    ) {
      const error =
        new Error(
          responseData?.error ||
          fallbackMessage
        );

      error.code =
        responseData?.error_code ||
        responseData?.reason_code ||
        "REQUEST_DENIED";

      error.status =
        response.status;

      error.retryable =
        responseData?.retryable ===
          true ||
        response.status >= 500;

      throw error;
    }

    return responseData;
  }

  function isServiceFailure(error) {
    return (
      error?.name === "AbortError" ||
      error?.retryable === true ||
      Number(error?.status) >= 500 ||
      error?.code === "NETWORK_ERROR"
    );
  }

  function getCartUrl() {
    return `${
      window.Shopify?.routes?.root ||
      "/"
    }cart.js`;
  }

  async function loadCart({
    force = false
  } = {}) {
    if (window.SupplyMeCartState) return window.SupplyMeCartState.load({ force });
    if (
      cartPromise &&
      !force
    ) {
      return cartPromise;
    }

    cartPromise =
      fetch(
        getCartUrl(),
        {
          method: "GET",
          credentials: "same-origin",
          headers: {
            Accept:
              "application/json"
          }
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              "Unable to read cart"
            );
          }

          return response.json();
        })
        .catch((error) => {
          cartPromise = null;
          throw error;
        });

    return cartPromise;
  }

  function invalidateCartCache() {
    cartPromise = null;
    window.SupplyMeCartState?.invalidate();
  }

  function cartHasSupplyMeMembership(cart) {
    return window.SupplyMeCartState?.containsMembership(cart) === true;
  }

  function getStoredUpgradeReservationToken() {
    try {
      return window.sessionStorage.getItem(
        UPGRADE_RESERVATION_STORAGE_KEY
      );
    } catch (error) {
      return null;
    }
  }

  function storeUpgradeReservationToken(token) {
    try {
      window.sessionStorage.setItem(
        UPGRADE_RESERVATION_STORAGE_KEY,
        token
      );
    } catch (error) {
      // Reservation expiry remains the fallback when storage is unavailable.
    }
  }

  function clearStoredUpgradeReservationToken(token = null) {
    try {
      const storedToken = getStoredUpgradeReservationToken();
      if (!token || storedToken === token) {
        window.sessionStorage.removeItem(
          UPGRADE_RESERVATION_STORAGE_KEY
        );
      }
    } catch (error) {
      // No action is required when storage is unavailable.
    }
  }

  function clearContextCache() {
    contextCache.clear();
  }

  async function reconcileAfterNativeCartAdd(
    buyBox,
    control
  ) {
    const productId = String(
      buyBox?.dataset?.supplymeProductId || ""
    );

    if (!productId) {
      return;
    }

    if (control) {
      control.textContent = "Added ✓";
      control.removeAttribute("aria-busy");
    }

    const deadline = Date.now() + 5000;

    while (Date.now() < deadline) {
      await new Promise((resolve) =>
        window.setTimeout(resolve, 250)
      );

      try {
        invalidateCartCache();
        const cart = await loadCart({ force: true });

        if (!getCartProductIds(cart).includes(productId)) {
          continue;
        }

        clearContextCache();
        const context = await loadAccessContext(buyBox);
        applyContext(buyBox, context);
        return;
      } catch {
        /* Continue polling until the cart update or timeout. */
      }
    }

    if (control) {
      control.textContent =
        control.dataset.supplymeOriginalText ||
        control.dataset.addToCartText ||
        "Add to Cart";
    }
  }

  function getCartProductIds(cart) {
    if (
      !Array.isArray(
        cart?.items
      )
    ) {
      return [];
    }

    return [
      ...new Set(
        cart.items
          .map((item) =>
            item?.product_id != null
              ? String(
                  item.product_id
                )
              : null
          )
          .filter(Boolean)
      )
    ];
  }

  function getStorefrontPricing(
    buyBox
  ) {
    const sellingPriceCents =
      Number(
        buyBox.dataset
          .supplymeSellingPriceCents
      );

    const rawCompare =
      buyBox.dataset
        .supplymeCompareAtPriceCents;

    const compareAtPriceCents =
      rawCompare
        ? Number(rawCompare)
        : null;

    if (
      !Number.isInteger(
        sellingPriceCents
      )
    ) {
      return null;
    }

    return {
      selling_price_cents:
        sellingPriceCents,
      compare_at_price_cents:
        Number.isInteger(
          compareAtPriceCents
        )
          ? compareAtPriceCents
          : null,
      currency:
        buyBox.dataset
          .supplymeCurrency ||
        null,
      available_for_sale:
        buyBox.dataset
          .supplymeAvailable !==
        "false"
    };
  }

  async function loadAccessContext(
    buyBox
  ) {
    const contextUrl =
      buyBox.dataset
        .supplymeContextUrl;

    const productId =
      buyBox.dataset
        .supplymeProductId;

    const variantId =
      buyBox.dataset
        .supplymeVariantId;

    if (
      !contextUrl ||
      !productId
    ) {
      throw new Error(
        "SupplyMe context configuration is incomplete."
      );
    }

    const cart =
      await loadCart();

    const body = {
      product_id:
        productId,
      variant_id:
        variantId || null,
      cart: {
        token:
          cart?.token || null,
        product_ids:
          getCartProductIds(
            cart
          )
      },
      pricing:
        getStorefrontPricing(
          buyBox
        )
    };

    const cacheKey =
      JSON.stringify(body);

    if (
      contextCache.has(
        cacheKey
      )
    ) {
      return contextCache.get(
        cacheKey
      );
    }

    const contextPromise =
      withTimeout(
        (signal) =>
          fetch(
            contextUrl,
            {
              method: "POST",
              credentials:
                "same-origin",
              signal,
              headers: {
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json"
              },
              body:
                JSON.stringify(body)
            }
          ),
        CONTEXT_TIMEOUT_MS
      )
        .then((response) =>
          parseJsonResponse(
            response,
            "We could not check your personalized access."
          )
        )
        .catch((error) => {
          contextCache.delete(
            cacheKey
          );

          if (
            error?.name ===
            "TypeError"
          ) {
            error.code =
              "NETWORK_ERROR";
            error.retryable =
              true;
          }

          throw error;
        });

    contextCache.set(
      cacheKey,
      contextPromise
    );

    return contextPromise;
  }

  function formatMoney(
    value,
    centsValue,
    currency
  ) {
    let numericValue = null;

    if (
      Number.isFinite(
        Number(centsValue)
      )
    ) {
      numericValue =
        Number(centsValue) /
        100;
    } else if (
      Number.isFinite(
        Number(value)
      )
    ) {
      numericValue =
        Number(value);
    }

    if (
      numericValue === null ||
      !Number.isFinite(
        numericValue
      )
    ) {
      return "";
    }

    if (numericValue === 0) {
      return "FREE";
    }

    try {
      return new Intl.NumberFormat(
        undefined,
        {
          style: "currency",
          currency:
            currency || "USD"
        }
      ).format(
        numericValue
      );
    } catch {
      return `$${numericValue.toFixed(
        2
      )}`;
    }
  }

  function renderPersonalizedPricing(
    buyBox,
    pricing
  ) {
    const root =
      buyBox.querySelector(
        "[data-supplyme-personalized-pricing]"
      );

    if (!root) {
      return;
    }

    if (
      !pricing ||
      pricing.available !== true ||
      pricing.display_mode ===
        "owned"
    ) {
      root.hidden = true;
      return;
    }

    const regularRow =
      root.querySelector(
        "[data-supplyme-pricing-regular-row]"
      );

    const regularValue =
      root.querySelector(
        "[data-supplyme-pricing-regular]"
      );

    const customerRow =
      root.querySelector(
        "[data-supplyme-pricing-customer-row]"
      );

    const customerValue =
      root.querySelector(
        "[data-supplyme-pricing-customer]"
      );

    const savingsRow =
      root.querySelector(
        "[data-supplyme-pricing-savings-row]"
      );

    const savingsValue =
      root.querySelector(
        "[data-supplyme-pricing-savings]"
      );

    const comparison =
      root.querySelector(
        "[data-supplyme-pricing-comparison]"
      );

    const comparisonLabel =
      root.querySelector(
        "[data-supplyme-pricing-comparison-label]"
      );

    const comparisonValue =
      root.querySelector(
        "[data-supplyme-pricing-comparison-value]"
      );

    const baselinePrice =
      pricing.selling_price != null
        ? Number(
            pricing.selling_price
          )
        : Number(
            pricing.regular_price
          );

    const baselineCents =
      pricing.selling_price_cents != null
        ? Number(
            pricing
              .selling_price_cents
          )
        : Number(
            pricing
              .regular_price_cents
          );

    const customerPrice =
      pricing.customer_price != null
        ? Number(
            pricing.customer_price
          )
        : null;

    const showCustomer =
      customerPrice !== null &&
      Number.isFinite(
        customerPrice
      );

    const showRegular =
      Number.isFinite(
        baselinePrice
      ) &&
      showCustomer &&
      baselinePrice !==
        customerPrice;

    if (
      regularRow &&
      regularValue
    ) {
      regularValue.textContent =
        showRegular
          ? formatMoney(
              baselinePrice,
              baselineCents,
              pricing.currency
            )
          : "";

      regularRow.hidden =
        !showRegular;
    }

    if (
      customerRow &&
      customerValue
    ) {
      customerValue.textContent =
        showCustomer
          ? formatMoney(
              customerPrice,
              pricing
                .customer_price_cents,
              pricing.currency
            )
          : "";

      customerRow.hidden =
        !showCustomer;
    }

    const savingsAmount =
      Number(
        pricing.savings_amount
      );

    const showSavings =
      Number.isFinite(
        savingsAmount
      ) &&
      savingsAmount > 0;

    if (
      savingsRow &&
      savingsValue
    ) {
      savingsValue.textContent =
        showSavings
          ? formatMoney(
              savingsAmount,
              pricing
                .savings_amount_cents,
              pricing.currency
            )
          : "";

      savingsRow.hidden =
        !showSavings;
    }

    const comparisonPrice =
      pricing.comparison_price != null
        ? Number(
            pricing.comparison_price
          )
        : null;

    const showComparison =
      comparisonPrice !== null &&
      Number.isFinite(
        comparisonPrice
      ) &&
      typeof pricing
        .comparison_label ===
        "string" &&
      pricing
        .comparison_label
        .trim()
        .length > 0;

    if (
      comparison &&
      comparisonLabel &&
      comparisonValue
    ) {
      comparisonLabel.textContent =
        showComparison
          ? pricing
              .comparison_label
              .trim()
          : "";

      comparisonValue.textContent =
        showComparison
          ? formatMoney(
              comparisonPrice,
              pricing
                .comparison_price_cents,
              pricing.currency
            )
          : "";

      comparison.hidden =
        !showComparison;
    }

    root.dataset
      .supplymePricingState =
      pricing.pricing_state ||
      "";

    root.dataset
      .supplymePricingReason =
      pricing.pricing_reason ||
      "";

    root.hidden =
      !(
        showRegular ||
        showCustomer ||
        showSavings ||
        showComparison
      );
  }

  function getPanelTone(
    stateId
  ) {
    const tones = {
      guest: "info",
      free_account: "info",
      unlimited_member:
        "membership",
      included_unlimited_free:
        "membership",
      included_lifetime_unlimited_free:
        "membership",
      included_annual_all_access:
        "membership",
      included_lifetime_all_access:
        "membership",
      previously_purchased:
        "success",
      already_in_cart:
        "success",
      purchase_required:
        "purchase",
      daily_limit_reached:
        "limit",
      product_unavailable:
        "warning",
      unknown: "error"
    };

    return tones[stateId] ||
      "info";
  }

  function renderAccessPanel(
    buyBox,
    accessPanel
  ) {
    const panel =
      buyBox.querySelector(
        "[data-supplyme-access-panel]"
      );

    if (
      !panel ||
      !accessPanel ||
      accessPanel.visible !== true
    ) {
      return;
    }

    const status =
      panel.querySelector(
        "[data-supplyme-access-status]"
      );

    const explanation =
      panel.querySelector(
        "[data-supplyme-access-explanation]"
      );

    const detail =
      panel.querySelector(
        "[data-supplyme-access-detail]"
      );

    if (status) {
      status.textContent =
        accessPanel.status || "";
    }

    if (explanation) {
      explanation.textContent =
        accessPanel
          .primary_explanation ||
        "";
    }

    if (detail) {
      const detailText =
        accessPanel
          .supporting_detail ||
        "";

      detail.textContent =
        detailText;
      detail.hidden =
        detailText.length === 0;
    }

    panel.dataset
      .supplymeAccessTone =
      getPanelTone(
        accessPanel.state
      );

    panel.dataset
      .supplymeAccessState =
      accessPanel.state || "";

    panel.hidden = false;
  }

  function resolveDestination(
    buyBox,
    destination
  ) {
    const destinations = {
      customer_account:
        buyBox.dataset
          .supplymeAccountUrl,
      customer_account_login:
        buyBox.dataset
          .supplymeLoginUrl,
      membership_comparison:
        buyBox.dataset
          .supplymeMembershipUrl,
      cart:
        buyBox.dataset
          .supplymeCartUrl
    };

    return (
      destinations[
        destination
      ] ||
      destination ||
      null
    );
  }

  function renderSecondaryAction(
    buyBox,
    cta
  ) {
    const wrap =
      buyBox.querySelector(
        "[data-supplyme-secondary-actions]"
      );

    const link =
      buyBox.querySelector(
        "[data-supplyme-secondary-cta]"
      );

    if (!wrap || !link) {
      return;
    }

    const label =
      cta?.secondary_label ||
      "";

    const destination =
      resolveDestination(
        buyBox,
        cta
          ?.secondary_destination
      );

    if (
      !label ||
      !destination
    ) {
      wrap.hidden = true;
      return;
    }

    link.textContent = label;
    link.href = destination;
    wrap.hidden = false;
  }

  function renderTrust(
    buyBox,
    trust
  ) {
    const root =
      buyBox.querySelector(
        "[data-supplyme-trust]"
      );

    const list =
      buyBox.querySelector(
        "[data-supplyme-trust-list]"
      );

    if (!root || !list) {
      return;
    }

    const messages =
      Array.isArray(
        trust?.messages
      )
        ? trust.messages
            .map((message) =>
              message?.text
                ? String(
                    message.text
                  )
                : ""
            )
            .filter(Boolean)
            .slice(0, 3)
        : [];

    if (
      messages.length === 0
    ) {
      root.hidden = true;
      return;
    }

    list.replaceChildren(
      ...messages.map(
        (message) => {
          const item =
            document.createElement(
              "li"
            );

          item.className =
            "supplyme-trust__item";

          item.textContent =
            message;

          return item;
        }
      )
    );

    root.hidden = false;
  }

  function renderBuyBoxDecision(
    buyBox,
    context
  ) {
    const form =
      buyBox.querySelector(
        FORM_SELECTOR
      );

    const control =
      getPrimaryControl(
        buyBox
      );

    const microcopy =
      buyBox.querySelector(
        "[data-supplyme-microcopy]"
      );

    const cta =
      context?.cta || {};

    const buyBoxContext =
      context?.buy_box || {};

    const action =
      cta.primary_action ||
      buyBoxContext
        .primary_action ||
      null;

    const label =
      cta.primary_label ||
      buyBoxContext
        .primary_label ||
      null;

    buyBox.dataset
      .supplymePrimaryAction =
      action || "";

    if (control && label) {
      control.textContent =
        label;
    }

    if (microcopy) {
      microcopy.textContent =
        buyBoxContext
          .microcopy ||
        microcopy.dataset
          .supplymeFallbackText ||
        "";
    }

    renderSecondaryAction(
      buyBox,
      cta
    );

    if (!control) {
      return;
    }

    delete control.dataset
      .supplymeNavigateUrl;
    delete control.dataset
      .supplymeMembershipOfferAction;

    if (
      action ===
        "view_account" ||
      action ===
        "view_cart" ||
      action ===
        "join_membership"
    ) {
      if (
        control instanceof
        HTMLButtonElement
      ) {
        control.type =
          "button";
        control.removeAttribute(
          "name"
        );
      }

      const destination =
        resolveDestination(
          buyBox,
          cta.destination
        );

      if (destination) {
        control.dataset
          .supplymeNavigateUrl =
          destination;
      }

      if (
        "disabled" in control
      ) {
        control.disabled =
          !destination;
      }

      return;
    }

    if (
      action ===
        "start_free_trial"
    ) {
      if (
        control instanceof
        HTMLButtonElement
      ) {
        control.type =
          "button";
        control.removeAttribute(
          "name"
        );
      }

      const offer =
        cta.membership_offer || {};
      control.dataset
        .supplymeMembershipOfferAction =
        "start_free_trial";
      control.dataset
        .supplymeMembershipVariantId =
        offer.variant_id || "";
      control.dataset
        .supplymeMembershipSellingPlanId =
        offer.selling_plan_id || "";
      control.disabled =
        !offer.variant_id ||
        !offer.selling_plan_id;
      return;
    }

    if (
      control instanceof
      HTMLButtonElement
    ) {
      control.type = "submit";
      control.name = "add";
      control.disabled =
        buyBox.dataset
          .supplymeAvailable ===
        "false";
    }

    if (form) {
      form.dataset
        .supplymeDecisionReady =
        "true";
    }
  }

  function applyContext(
    buyBox,
    context
  ) {
    renderPersonalizedPricing(
      buyBox,
      context?.pricing
    );

    renderAccessPanel(
      buyBox,
      context?.access_panel
    );

    renderBuyBoxDecision(
      buyBox,
      context
    );

    renderTrust(
      buyBox,
      context?.trust
    );

    buyBox.dataset
      .supplymeContextStatus =
      "ready";
  }

  function enableFallbackProperty(
    form
  ) {
    const input =
      form?.querySelector(
        "[data-supplyme-fallback-checkout]"
      );

    if (!input) {
      return;
    }

    input.value = "1";
    input.disabled = false;
  }

  function activateFallback(
    buyBox,
    reason
  ) {
    buyBox.dataset
      .supplymeContextStatus =
      "fallback";

    buyBox.dataset
      .supplymeFailOpen =
      "true";

    const control =
      getPrimaryControl(
        buyBox
      );

    if (
      control instanceof
      HTMLButtonElement
    ) {
      control.type = "submit";
      control.name = "add";
      control.disabled =
        buyBox.dataset
          .supplymeAvailable ===
        "false";

      control.textContent =
        control.dataset
          .supplymeFallbackLabel ||
        control.dataset
          .addToCartText ||
        "Add to Cart";
    }

    const secondary =
      buyBox.querySelector(
        "[data-supplyme-secondary-actions]"
      );

    if (secondary) {
      secondary.hidden = true;
    }

    console.warn(
      "SupplyMe personalization fallback active:",
      reason
    );
  }

  async function requestAction(
    url,
    payload
  ) {
    try {
      return await withTimeout(
        (signal) =>
          fetch(
            url,
            {
              method: "POST",
              credentials:
                "same-origin",
              signal,
              headers: {
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json"
              },
              body:
                JSON.stringify(
                  payload
                )
            }
          ),
        ACTION_TIMEOUT_MS
      ).then((response) =>
        parseJsonResponse(
          response,
          "We could not prepare this item for checkout."
        )
      );
    } catch (error) {
      if (
        error?.name ===
        "TypeError"
      ) {
        error.code =
          "NETWORK_ERROR";
        error.retryable = true;
      }

      throw error;
    }
  }

  function releaseNativeSubmit(
    form,
    submitButton
  ) {
    form.setAttribute(
      NATIVE_SUBMIT_ATTRIBUTE,
      "true"
    );

    form.requestSubmit(
      submitButton
    );
  }

  async function failOpenSubmit(
    buyBox,
    form,
    submitButton,
    reason
  ) {
    activateFallback(
      buyBox,
      reason
    );

    enableFallbackProperty(
      form
    );

    setButtonLoading(
      submitButton,
      false
    );

    releaseNativeSubmit(
      form,
      submitButton
    );
  }

  async function handleFreeSubmit({
    event,
    form,
    buyBox
  }) {
    if (
      buyBox.dataset
        .supplymeFailOpen ===
      "true"
    ) {
      enableFallbackProperty(
        form
      );
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitButton =
      event.submitter ||
      form.querySelector(
        'button[type="submit"]'
      );

    hideError(form);

    const reserveUrl =
      buyBox.dataset
        .supplymeReserveUrl;

    if (!reserveUrl) {
      await failOpenSubmit(
        buyBox,
        form,
        submitButton,
        "reserve URL missing"
      );
      return;
    }

    setButtonLoading(
      submitButton,
      true
    );

    try {
      const cart =
        await loadCart();

      const result =
        await requestAction(
          reserveUrl,
          {
            product_id:
              buyBox.dataset
                .supplymeProductId,
            variant_id:
              buyBox.dataset
                .supplymeVariantId ||
              null,
            cart_token:
              cart?.token || null
          }
        );

      const token =
        result
          ?.reservation
          ?.token
          ?.trim();

      if (!token) {
        throw new Error(
          "Reservation token missing"
        );
      }

      const input =
        form.querySelector(
          "[data-supplyme-reservation-token]"
        );

      if (!input) {
        throw new Error(
          "Reservation field missing"
        );
      }

      input.value = token;
      input.disabled = false;

      setButtonLoading(
        submitButton,
        false
      );

      releaseNativeSubmit(
        form,
        submitButton
      );

      void reconcileAfterNativeCartAdd(
        buyBox,
        submitButton
      );
    } catch (error) {
      if (
        isServiceFailure(
          error
        )
      ) {
        await failOpenSubmit(
          buyBox,
          form,
          submitButton,
          error?.code ||
          error?.message
        );
        return;
      }

      setButtonLoading(
        submitButton,
        false
      );

      showError(
        form,
        error instanceof Error
          ? error.message
          : "This printable could not be added to your cart."
      );
    }
  }

  async function handlePaidSubmit({
    event,
    form,
    buyBox
  }) {
    if (
      buyBox.dataset
        .supplymeFailOpen ===
      "true"
    ) {
      enableFallbackProperty(
        form
      );
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitButton =
      event.submitter ||
      form.querySelector(
        'button[type="submit"]'
      );

    hideError(form);

    const action =
      buyBox.dataset
        .supplymePrimaryAction ||
      "add_to_cart";

    const isMembershipAction =
      action ===
        "annual_membership_checkout" ||
      action ===
        "lifetime_membership_checkout" ||
      action ===
        "unlimited_membership_checkout";

    const url =
      isMembershipAction
        ? buyBox.dataset
            .supplymeMembershipGrantUrl
        : buyBox.dataset
            .supplymePurchaseGrantUrl;

    if (!url) {
      await failOpenSubmit(
        buyBox,
        form,
        submitButton,
        "grant URL missing"
      );
      return;
    }

    setButtonLoading(
      submitButton,
      true
    );

    try {
      const result =
        await requestAction(
          url,
          {
            product_id:
              buyBox.dataset
                .supplymeProductId,
            variant_id:
              buyBox.dataset
                .supplymeVariantId ||
              null
          }
        );

      const token =
        result
          ?.grant
          ?.token
          ?.trim();

      if (!token) {
        throw new Error(
          "Acquisition grant token missing"
        );
      }

      const input =
        form.querySelector(
          "[data-supplyme-acquisition-grant]"
        );

      if (!input) {
        throw new Error(
          "Acquisition grant field missing"
        );
      }

      input.value = token;
      input.disabled = false;

      setButtonLoading(
        submitButton,
        false
      );

      releaseNativeSubmit(
        form,
        submitButton
      );

      void reconcileAfterNativeCartAdd(
        buyBox,
        submitButton
      );
    } catch (error) {
      if (
        isServiceFailure(
          error
        )
      ) {
        await failOpenSubmit(
          buyBox,
          form,
          submitButton,
          error?.code ||
          error?.message
        );
        return;
      }

      setButtonLoading(
        submitButton,
        false
      );

      showError(
        form,
        error instanceof Error
          ? error.message
          : "This printable could not be added to your cart."
      );
    }
  }

  async function initializeBuyBox(
    buyBox
  ) {
    if (
      buyBox.dataset
        .supplymeInitialized ===
      "true"
    ) {
      return;
    }

    buyBox.dataset
      .supplymeInitialized =
      "true";

    if (
      buyBox.dataset
        .supplymeAuthenticated !==
      "true"
    ) {
      buyBox.dataset
        .supplymeContextStatus =
        "guest";
      return;
    }

    const control =
      getPrimaryControl(
        buyBox
      );

    setButtonLoading(
      control,
      true
    );

    try {
      const context =
        await loadAccessContext(
          buyBox
        );

      applyContext(
        buyBox,
        context
      );

      setButtonLoading(
        control,
        false
      );
    } catch (error) {
      activateFallback(
        buyBox,
        error?.code ||
        error?.message ||
        "context unavailable"
      );

      setButtonLoading(
        control,
        false
      );
    }
  }

  function initialize() {
    const buyBoxes =
      document.querySelectorAll(
        ROOT_SELECTOR
      );

    buyBoxes.forEach(
      initializeBuyBox
    );

    document
      .querySelectorAll("[data-supplyme-upgrade-credit]")
      .forEach(initializeUpgradeCreditOffer);
  }

  async function initializeUpgradeCreditOffer(root) {
    if (root.dataset.supplymeInitialized === "true") return;
    root.dataset.supplymeInitialized = "true";

    try {
      await reconcileStoredUpgradeReservation(root);

      const cart = await loadCart();
      const membershipInCart = cartHasSupplyMeMembership(cart);

      const context = await requestAction(root.dataset.contextUrl, {
        product_id: root.dataset.productId || null,
        variant_id: root.dataset.variantId || null
      });
      const credit = context.upgrade_credit;
      const presentation = context.personalized_membership_options || {};
      const offers = Array.isArray(presentation.offers) ? presentation.offers : [];
      const heading = root.querySelector("[data-membership-options-heading]");
      const subheading = root.querySelector("[data-membership-options-subheading]");
      const status = root.querySelector("[data-membership-options-status]");

      if (!credit?.enabled || credit.shadow_mode) {
        root.hidden = true;
        return;
      }

      if (heading) {
        heading.textContent = presentation.heading || "Your personalized membership options";
      }
      if (subheading) {
        subheading.textContent = presentation.subheading || "";
        subheading.hidden = !presentation.subheading;
      }
      if (status) {
        status.textContent = presentation.status || "";
        status.hidden = !presentation.status;
      }

      renderPersonalizedOffers(root, offers);
      root.dataset.supplymeOfferEligible = offers.length > 0 ? "true" : "false";
      root.hidden = membershipInCart || offers.length === 0;
    } catch (error) {
      root.hidden = true;
      console.warn("SupplyMe Upgrade Credit offer unavailable:", error);
    }
  }

  async function reconcileStoredUpgradeReservation(root) {
    const reservationToken = getStoredUpgradeReservationToken();
    if (!reservationToken || !root.dataset.releaseUrl) return;

    const cart = await loadCart({ force: true });
    const annualVariantId = String(root.dataset.annualVariantId || "");
    const annualSellingPlanId = String(
      root.dataset.annualSellingPlanId || ""
    );
    const qualifyingItemPresent = (cart.items || []).some((item) => {
      const itemVariantId = String(item.variant_id || item.id || "");
      const itemSellingPlanId = String(
        item.selling_plan_allocation?.selling_plan?.id || ""
      );

      return itemVariantId === annualVariantId &&
        (!annualSellingPlanId || itemSellingPlanId === annualSellingPlanId);
    });

    if (qualifyingItemPresent) return;

    await requestAction(root.dataset.releaseUrl, {
      reservation_token: reservationToken
    });
    clearStoredUpgradeReservationToken(reservationToken);
  }

  function formatUpgradeCreditMoney(cents) {
    return (Math.max(0, Number(cents || 0)) / 100).toFixed(2);
  }

  function appendBenefitContent(item, benefit) {
    if (typeof benefit === "string") {
      item.textContent = benefit;
      return;
    }

    const segments = Array.isArray(benefit?.segments)
      ? benefit.segments
      : [];

    segments.forEach((segment) => {
      const text = String(segment?.text || "");
      if (!text) return;

      let node = document.createTextNode(text);

      if (segment?.emphasis === true) {
        const emphasis = document.createElement("em");
        emphasis.appendChild(node);
        node = emphasis;
      }

      if (segment?.strong === true) {
        const strong = document.createElement("strong");
        strong.appendChild(node);
        node = strong;
      }

      item.appendChild(node);
    });
  }

  function renderPersonalizedOffers(root, offers) {
    const grid = root.querySelector("[data-personalized-offer-grid]");
    const template = root.querySelector("[data-personalized-offer-template]");
    if (!grid || !template) return;
    grid.replaceChildren();

    offers.forEach((offer) => {
      const fragment = template.content.cloneNode(true);
      const card = fragment.querySelector("[data-personalized-offer-card]");
      const summary = fragment.querySelector(".supplyme-upgrade-credit__membership-summary");
      const heading = fragment.querySelector("[data-personalized-offer-heading]");
      const scope = fragment.querySelector("[data-personalized-offer-scope]");
      const imageWrap = fragment.querySelector("[data-personalized-offer-image-wrap]");
      const image = fragment.querySelector("[data-personalized-offer-image]");
      const message = fragment.querySelector("[data-personalized-offer-message]");
      const pricing = fragment.querySelector("[data-personalized-offer-pricing]");
      const benefits = fragment.querySelector("[data-personalized-offer-benefits]");
      const disclosure = fragment.querySelector("[data-personalized-offer-disclosure]");
      const deliveryMicrocopy = fragment.querySelector("[data-personalized-offer-delivery-microcopy]");
      const button = fragment.querySelector("[data-personalized-offer-action]");

      heading.textContent = offer.heading || "Membership Offer";
      if (scope) {
        scope.textContent = offer.scope_label || "";
        scope.hidden = !offer.scope_label;
      }
      if (imageWrap && image && offer.image_url) {
        image.src = offer.image_url;
        image.alt = offer.image_alt || offer.heading || "Membership option";
        imageWrap.hidden = false;
        summary?.classList.add("supplyme-upgrade-credit__membership-summary--has-image");
      }
      message.textContent = offer.message || "";
      (offer.price_rows || []).forEach((row) => {
        const line = document.createElement("p");
        line.className = `supplyme-upgrade-credit__${row.style || "regular"}`;
        line.append(document.createTextNode(`${row.label || "Price"} `));
        const value = document.createElement(row.style === "regular" ? "s" : "strong");
        value.textContent = Number(row.value_cents) === 0 && offer.zero_price_label
          ? offer.zero_price_label
          : `$${formatUpgradeCreditMoney(row.value_cents)}`;
        line.append(value);
        pricing.append(line);
      });
      (offer.benefits || []).forEach((benefit) => {
        const item = document.createElement("li");
        appendBenefitContent(item, benefit);
        benefits.appendChild(item);
      });
      benefits.hidden = benefits.children.length === 0;
      disclosure.textContent = offer.disclosure || "";
      if (deliveryMicrocopy) {
        deliveryMicrocopy.textContent = offer.delivery_microcopy || "";
        deliveryMicrocopy.hidden = !offer.delivery_microcopy;
      }
      button.textContent = offer.cta_label || "Choose This Offer";
      button.dataset.actionType = offer.action?.type || "";
      button.dataset.target = offer.action?.target || "";
      button.dataset.claimEligible = offer.action?.claim_eligible ? "true" : "false";
      button.dataset.offerKey = offer.action?.offer_key || "";
      button.dataset.offerType = offer.action?.offer_type || "";
      button.dataset.membershipVariantId = offer.action?.membership_variant_id || "";
      button.dataset.membershipSellingPlanId = offer.action?.membership_selling_plan_id || "";
      button.dataset.printableVariantId = offer.action?.printable_variant_id || "";
      button.dataset.printableTitle = offer.action?.printable_title || "";
      button.dataset.deliveryMode = offer.action?.delivery_mode || "cart_bundle";
      card.dataset.offerKey = offer.offer_key;
      card.open = false;
      grid.appendChild(fragment);
    });
    grid.hidden = offers.length === 0;
  }

  async function addMembershipOfferItemsToCart({
    membershipVariantId,
    membershipSellingPlanId,
    printableVariantId = null,
    membershipProperties = null
  }) {
    const routesRoot = window.Shopify?.routes?.root || "/";
    const membershipItem = {
      id: Number(membershipVariantId),
      quantity: 1
    };
    if (membershipSellingPlanId) {
      membershipItem.selling_plan = Number(membershipSellingPlanId);
    }
    if (membershipProperties && typeof membershipProperties === "object") {
      membershipItem.properties = membershipProperties;
    }
    const items = [membershipItem];
    if (printableVariantId) {
      items.push({ id: Number(printableVariantId), quantity: 1 });
    }
    return fetch(`${routesRoot}cart/add.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ items })
    }).then((response) => parseJsonResponse(
      response,
      "We could not add this membership offer to your cart."
    ));
  }

  async function showMembershipOfferCart(button, cartUrl = "/cart") {
    const cartDrawer = document.querySelector("cart-drawer");

    if (
      cartDrawer &&
      typeof cartDrawer.refresh === "function" &&
      typeof cartDrawer.open === "function"
    ) {
      await cartDrawer.refresh(true);
      cartDrawer.open(button);
      return;
    }

    window.location.href = cartUrl;
  }

  async function redirectIfMembershipAlreadyInCart(button, cartUrl = "/cart") {
    const cart = await loadCart({ force: true });
    if (!cartHasSupplyMeMembership(cart)) return false;
    button.textContent = "Membership Added";
    await showMembershipOfferCart(button, cartUrl);
    button.disabled = false;
    return true;
  }

  function waitForCartDiscount(delayMs) {
    return new Promise((resolve) =>
      window.setTimeout(resolve, delayMs)
    );
  }

  async function applyDiscountToCart(codes) {
    const routesRoot = window.Shopify?.routes?.root || "/";
    const delays = [0, 350, 900, 1600];
    const requestedCodes = (Array.isArray(codes) ? codes : [codes]).filter(Boolean);
    const discountValue = requestedCodes.join(",");

    for (const delay of delays) {
      if (delay) {
        await waitForCartDiscount(delay);
      }

      const cart = await fetch(`${routesRoot}cart/update.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({ discount: discountValue })
      }).then((response) => parseJsonResponse(
        response,
        "We could not apply the membership offer discount."
      ));

      const allCodesApplied = requestedCodes.every((requestedCode) =>
        (cart.discount_codes || []).some((item) =>
          String(item?.code || "").toLowerCase() ===
            String(requestedCode).toLowerCase() &&
          item?.applicable === true
        )
      );

      if (allCodesApplied) {
        invalidateCartCache();
        return cart;
      }
    }

    throw new Error(
      "The membership and printable were added, but Shopify did not apply all authorized savings. Please remove both items and try again."
    );
  }

  document.addEventListener("supplyme:cart-state:changed", (event) => {
    const membershipInCart = event.detail?.containsMembership === true;
    document.querySelectorAll("[data-supplyme-upgrade-credit]").forEach((root) => {
      const eligible = root.dataset.supplymeOfferEligible === "true";
      root.hidden = membershipInCart || !eligible;
    });
  });

  document.addEventListener("click", async (event) => {
    const offerButton = event.target.closest("[data-personalized-offer-action]");
    if (!offerButton) return;

    if (offerButton.dataset.actionType === "membership_acquisition") {
      const root = offerButton.closest("[data-supplyme-upgrade-credit]");
      const error = root.querySelector("[data-upgrade-error]");
      const originalLabel = offerButton.textContent;
      event.preventDefault();
      offerButton.disabled = true;
      offerButton.textContent = "Preparing your offer...";
      error.hidden = true;

      try {
        if (await redirectIfMembershipAlreadyInCart(
          offerButton,
          root.dataset.cartUrl || "/cart"
        )) return;
        let code = null;
        let authorizationResult = null;
        if (offerButton.dataset.offerType === "membership_printable_bundle") {
          authorizationResult = await requestAction(
            root.dataset.membershipOfferAuthorizeUrl,
            {
              offer_key: offerButton.dataset.offerKey,
              product_id: root.dataset.productId,
              variant_id: root.dataset.variantId
            }
          );
          code = authorizationResult.authorization.discount_code;
        }

        const deliveryMode = authorizationResult?.authorization?.delivery_mode ||
          offerButton.dataset.deliveryMode || "cart_bundle";
        const complimentary = deliveryMode === "complimentary_order";
        const authorizationId = authorizationResult?.authorization?.id || "";
        const membershipProperties = complimentary ? {
          "FREE printable delivered after checkout":
            offerButton.dataset.printableTitle || "Selected paid printable",
          "_supplyme_membership_offer_authorization_id": authorizationId,
          "_supplyme_reward_product_id": root.dataset.productId || "",
          "_supplyme_reward_variant_id": root.dataset.variantId || ""
        } : null;

        await addMembershipOfferItemsToCart({
          membershipVariantId: offerButton.dataset.membershipVariantId,
          membershipSellingPlanId: offerButton.dataset.membershipSellingPlanId,
          printableVariantId: complimentary
            ? null
            : offerButton.dataset.printableVariantId || null,
          membershipProperties
        });

        if (code) {
          await applyDiscountToCart(code);
        }

        await showMembershipOfferCart(
          offerButton,
          root.dataset.cartUrl || "/cart"
        );
        offerButton.textContent = "Membership Added";
        offerButton.disabled = false;
      } catch (requestError) {
        error.textContent = requestError.message ||
          "We could not prepare this membership offer.";
        error.hidden = false;
        offerButton.disabled = false;
        offerButton.textContent = originalLabel;
      }
      return;
    }

    if (offerButton.dataset.actionType !== "upgrade_credit") return;
    const button = offerButton;
    const root = button.closest("[data-supplyme-upgrade-credit]");
    const target = button.dataset.target;
    const error = root.querySelector("[data-upgrade-error]");
    const originalLabel = button.textContent;
    let reservationToken = null;
    event.preventDefault();
    button.disabled = true;
    button.textContent = "Preparing your price...";
    error.hidden = true;

    try {
      if (target === "annual_all_access") {
        const reservation = await requestAction(root.dataset.reserveUrl, {
          target: "annual_all_access"
        });
        reservationToken = reservation.reservation.token;
        storeUpgradeReservationToken(reservationToken);
        const authorization = await requestAction(root.dataset.discountUrl, {
          reservation_token: reservationToken,
          product_id: root.dataset.productId,
          variant_id: root.dataset.variantId
        });
        const codes = authorization.authorization.discount_codes || [
          authorization.authorization.discount_code
        ];

        await addMembershipOfferItemsToCart({
          membershipVariantId: root.dataset.annualVariantId,
          membershipSellingPlanId: root.dataset.annualSellingPlanId,
          printableVariantId: authorization.authorization.printable_variant_id
        });

        await applyDiscountToCart(codes);
        window.location.href = root.dataset.cartUrl || "/cart";
        return;
      }

      if (button.dataset.claimEligible === "true") {
        await requestAction(root.dataset.claimUrl, {});
        window.location.href = root.dataset.accountUrl || "/account";
        return;
      }

      const authorization = await requestAction(root.dataset.discountUrl, {
        target: "lifetime_all_access",
        product_id: root.dataset.productId,
        variant_id: root.dataset.variantId
      });
      const codes = authorization.authorization.discount_codes || [
        authorization.authorization.discount_code
      ];

      await addMembershipOfferItemsToCart({
        membershipVariantId: root.dataset.lifetimeVariantId,
        printableVariantId: authorization.authorization.printable_variant_id
      });

      await applyDiscountToCart(codes);
      window.location.href = root.dataset.cartUrl || "/cart";
    } catch (requestError) {
      if (
        reservationToken &&
        root.dataset.releaseUrl
      ) {
        requestAction(
          root.dataset.releaseUrl,
          {
            reservation_token:
              reservationToken
          }
        )
          .then(() => {
            clearStoredUpgradeReservationToken(reservationToken);
          })
          .catch(() => {});
      }

      error.textContent = requestError.message || "We could not prepare this upgrade.";
      error.hidden = false;
      button.disabled = false;
      button.textContent = originalLabel;
    }
  });

  document.addEventListener("click", async (event) => {
    const control = event.target.closest(
      "[data-supplyme-membership-offer-action='start_free_trial']"
    );
    if (!control) return;
    event.preventDefault();
    const originalLabel = control.textContent;
    control.disabled = true;
    control.textContent = "Adding your free trial...";
    try {
      if (await redirectIfMembershipAlreadyInCart(control, "/cart")) return;
      await addMembershipOfferItemsToCart({
        membershipVariantId: control.dataset.supplymeMembershipVariantId,
        membershipSellingPlanId:
          control.dataset.supplymeMembershipSellingPlanId
      });
      window.location.href = "/cart";
    } catch (error) {
      control.disabled = false;
      control.textContent = originalLabel;
      const buyBox = control.closest(ROOT_SELECTOR);
      const form = buyBox?.querySelector(FORM_SELECTOR);
      if (form) showError(form, error.message || "We could not add the free trial.");
    }
  });

  document.addEventListener(
    "click",
    (event) => {
      const control =
        event.target.closest(
          "[data-supplyme-primary-cta]"
        );

      if (!control) {
        return;
      }

      const navigateUrl =
        control.dataset
          .supplymeNavigateUrl;

      if (!navigateUrl) {
        return;
      }

      event.preventDefault();

      window.location.href =
        navigateUrl;
    }
  );

  document.addEventListener(
    "submit",
    async (event) => {
      const form =
        event.target;

      if (
        !(
          form instanceof
          HTMLFormElement
        ) ||
        !form.matches(
          FORM_SELECTOR
        )
      ) {
        return;
      }

      if (
        form.hasAttribute(
          NATIVE_SUBMIT_ATTRIBUTE
        )
      ) {
        form.removeAttribute(
          NATIVE_SUBMIT_ATTRIBUTE
        );

        invalidateCartCache();
        return;
      }

      const buyBox =
        form.closest(
          ROOT_SELECTOR
        );

      if (!buyBox) {
        return;
      }

      const mode =
        buyBox.dataset
          .supplymeBuyBoxMode;

      if (
        mode ===
        "free-printable"
      ) {
        await handleFreeSubmit({
          event,
          form,
          buyBox
        });
        return;
      }

      if (
        mode ===
        "paid-printable"
      ) {
        await handlePaidSubmit({
          event,
          form,
          buyBox
        });
      }
    },
    true
  );

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }
})();
